import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  signal
} from '@angular/core';

import { AssistantAgentService } from './assistant-agent.service';

/**
 * Self-contained voice assistant widget: a floating button that opens a
 * right-side slide-out panel where the user can talk to the agent.
 *
 * Drop <app-assistant-widget /> into a layout to enable it globally.
 */
@Component({
  selector: 'app-assistant-widget',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './assistant-widget.component.html',
  styleUrl: './assistant-widget.component.scss'
})
export class AssistantWidgetComponent implements AfterViewChecked, OnDestroy {
  @ViewChild('history') private historyEl?: ElementRef<HTMLDivElement>;

  readonly isOpen = signal(false);
  readonly state = this.agent.state;

  constructor(public agent: AssistantAgentService) {}

  togglePanel(): void {
    this.isOpen.update(v => !v);
  }

  close(): void {
    this.isOpen.set(false);
  }

  /** Start a session: connect, then begin recording shortly after. */
  async start(): Promise<void> {
    try {
      await this.agent.connect();
      // Give the connection a moment to settle before streaming mic audio.
      setTimeout(() => this.agent.startRecording(), 1000);
    } catch {
      // Error surfaced via state.error.
    }
  }

  stop(): void {
    this.agent.disconnect();
  }

  toggleSession(): void {
    const s = this.state();
    if (s.isConnected || s.isRecording) {
      this.stop();
    } else {
      this.start();
    }
  }

  ngAfterViewChecked(): void {
    const el = this.historyEl?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }

  ngOnDestroy(): void {
    this.agent.disconnect();
  }
}
